import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Archive, Trash2, Save, Code2, Eye } from "lucide-react";
import DashboardShell from "@/pages/dashboard/DashboardShell";
import ProjectPreview from "@/pages/dashboard/ProjectPreview";
import {
  ARCHETYPE_LABELS,
  getProject,
  patchProject,
  deleteProject,
} from "@/lib/vault";
import { extractFormError } from "@/lib/api";

const STATUS_STYLE = {
  draft: "text-neutral-500 border-neutral-200",
  published: "text-emerald-300 border-emerald-400/40",
  archived: "text-neutral-400 border-neutral-200",
};

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [name, setName] = useState("");
  const [configText, setConfigText] = useState("");
  const [view, setView] = useState("preview"); // preview | json
  const [saving, setSaving] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getProject(id)
      .then((p) => {
        if (cancelled) return;
        setProject(p);
        setName(p.name);
        setConfigText(JSON.stringify(p.config || {}, null, 2));
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(extractFormError(err, "Couldn't load that project."));
        navigate("/dashboard/vault");
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleSave = async () => {
    if (!project) return;
    let config;
    try {
      config = JSON.parse(configText);
    } catch (_e) {
      toast.error("Config isn't valid JSON. Fix the syntax and try again.");
      return;
    }
    setSaving(true);
    try {
      const updated = await patchProject(project.id, { name: name.trim(), config });
      setProject(updated);
      setConfigText(JSON.stringify(updated.config || {}, null, 2));
      toast.success("Saved.");
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't save that project."));
    } finally {
      setSaving(false);
    }
  };

  const handleStatus = async (next) => {
    if (!project || project.status === next) return;
    setStatusUpdating(true);
    try {
      const updated = await patchProject(project.id, { status: next });
      setProject(updated);
      toast.success(
        next === "published" ? "Published." : next === "archived" ? "Archived." : "Status updated."
      );
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't update status."));
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!project) return;
    if (typeof window !== "undefined" && !window.confirm(`Delete "${project.name}"? This cannot be undone.`)) return;
    try {
      await deleteProject(project.id);
      toast.success("Project deleted.");
      navigate("/dashboard/vault", { replace: true });
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't delete that project."));
    }
  };

  if (!project) {
    return (
      <DashboardShell back="/dashboard/vault" backLabel="Vault">
        <p className="text-neutral-500 text-sm" data-testid="project-loading">
          Loading project...
        </p>
      </DashboardShell>
    );
  }

  // Parse config live for the Preview tab so users see edits without saving.
  let parsedConfig = project.config;
  try {
    parsedConfig = JSON.parse(configText);
  } catch (_e) {
    /* keep last good config */
  }

  return (
    <DashboardShell back="/dashboard/vault" backLabel="Vault">
      <section data-testid="project-hero" className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="overline">{ARCHETYPE_LABELS[project.archetype] || project.archetype}</p>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="project-name"
            className="font-serif text-3xl sm:text-4xl tracking-tight bg-transparent border-b border-neutral-200 focus:border-gold focus:outline-none w-full mt-4 pb-2"
          />
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <span
              className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${STATUS_STYLE[project.status]}`}
              data-testid="project-status"
            >
              {project.status}
            </span>
            <span className="text-xs text-neutral-400">
              Updated {new Date(project.updated_at).toLocaleString()}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {project.status !== "published" && (
            <button
              type="button"
              onClick={() => handleStatus("published")}
              disabled={statusUpdating}
              data-testid="project-publish"
              className="btn-aurora disabled:opacity-60"
            >
              <CheckCircle2 size={14} /> Publish
            </button>
          )}
          {project.status === "published" && (
            <button
              type="button"
              onClick={() => handleStatus("draft")}
              disabled={statusUpdating}
              data-testid="project-unpublish"
              className="btn-ghost"
            >
              Move to draft
            </button>
          )}
          <button
            type="button"
            onClick={() => handleStatus("archived")}
            disabled={statusUpdating || project.status === "archived"}
            data-testid="project-archive"
            className="btn-ghost disabled:opacity-50"
          >
            <Archive size={14} /> Archive
          </button>
          <button
            type="button"
            onClick={handleDelete}
            data-testid="project-delete"
            className="btn-ghost border-rose-400/40 text-rose-200 hover:text-rose-100 hover:border-rose-400"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </section>

      <section className="mt-10 sm:mt-12">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
          <div
            data-testid="project-tabs"
            className="inline-flex border border-neutral-200 bg-white"
          >
            <button
              type="button"
              onClick={() => setView("preview")}
              data-testid="project-tab-preview"
              aria-pressed={view === "preview"}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] flex items-center gap-2 ${
                view === "preview" ? "bg-gold/10 text-gold" : "text-neutral-500 hover:text-neutral-900"
              }`}
            >
              <Eye size={12} /> Preview
            </button>
            <button
              type="button"
              onClick={() => setView("json")}
              data-testid="project-tab-json"
              aria-pressed={view === "json"}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] flex items-center gap-2 border-l border-neutral-200 ${
                view === "json" ? "bg-gold/10 text-gold" : "text-neutral-500 hover:text-neutral-900"
              }`}
            >
              <Code2 size={12} /> Edit JSON
            </button>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="project-save"
            className="btn-aurora disabled:opacity-60"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save changes"}
          </button>
        </div>

        {view === "preview" ? (
          <ProjectPreview archetype={project.archetype} config={parsedConfig} />
        ) : (
          <textarea
            data-testid="project-json"
            value={configText}
            onChange={(e) => setConfigText(e.target.value)}
            rows={24}
            spellCheck={false}
            className="w-full bg-white border border-neutral-200 focus:border-gold focus:outline-none p-5 text-xs font-mono leading-relaxed text-neutral-700 resize-y"
          />
        )}
      </section>

      <section className="mt-14 border-t border-neutral-200/70 pt-8">
        <p className="overline">Need a different angle?</p>
        <p className="text-sm text-neutral-500 mt-2">
          Open the Studio Builder again with a fresh prompt to draft another variant.
        </p>
        <Link to="/dashboard/builder" className="btn-ghost mt-5 inline-flex" data-testid="project-new-variant">
          Draft another variant
        </Link>
      </section>
    </DashboardShell>
  );
}
